import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { api } from "@/api";
import { Seo } from "@/components/Seo";
import { MaskedLine } from "@/components/Motion";
import { WatermarkOverlay } from "@/components/WatermarkOverlay";

export default function Riders() {
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/riders").then((response) => setRiders(response.data)).catch(() => setRiders([])).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="riders-page" className="min-h-screen bg-onyx pt-32">
      <Seo title="Riders & Drivers" description="Browse mellowjmedia frames grouped by rider and driver across every event." />
      <section className="mx-auto max-w-[1600px] px-4 sm:px-8 lg:px-12">
        <p className="font-mono text-xs uppercase tracking-[0.42em] text-gold">The people</p>
        <h1 className="mt-6 font-display text-5xl font-light leading-none text-white sm:text-7xl">
          <MaskedLine>Riders &</MaskedLine>
          <MaskedLine delay={0.14}><span className="italic text-titanium">drivers.</span></MaskedLine>
        </h1>
        <p className="mt-8 max-w-xl text-base leading-8 text-zinc-400">Every frame a rider or driver appears in, gathered from across the archive. Open a profile to collect their moments.</p>
      </section>

      <section className="mx-auto max-w-[1600px] px-4 py-16 sm:px-8 lg:px-12 lg:py-24">
        {loading && <p data-testid="riders-loading-state" className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-500">Loading profiles</p>}
        {!loading && riders.length === 0 && (
          <div data-testid="riders-empty-state" className="border border-white/10 bg-carbon p-12 text-center">
            <p className="font-display text-3xl text-white">No rider profiles yet.</p>
            <p className="mt-3 text-sm text-zinc-500">Profiles appear automatically once photos are tagged with a rider or driver in the admin media library.</p>
          </div>
        )}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {riders.map((rider, index) => (
            <motion.div key={rider.slug} initial={{ opacity: 0, y: 32 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.08, duration: 0.65 }}>
              <Link data-testid={`rider-card-${rider.slug}`} to={`/riders/${rider.slug}`} className="group relative block overflow-hidden border border-white/10 bg-carbon">
                <div className="relative h-72 overflow-hidden">
                  <img src={rider.cover_url} alt={`${rider.name} featured frame`} className="h-full w-full object-cover opacity-75 transition duration-700 group-hover:scale-[1.04] group-hover:opacity-95" />
                  <WatermarkOverlay />
                  <div className="absolute inset-0 bg-gradient-to-t from-onyx via-transparent to-transparent" />
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <h2 className="font-display text-3xl font-light text-white">{rider.name}</h2>
                    <ArrowRight className="h-4 w-4 text-gold transition-transform group-hover:translate-x-1" />
                  </div>
                  <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-gold">{rider.photo_count} frame{rider.photo_count === 1 ? "" : "s"}</p>
                  <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">{rider.events.join(" · ")}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
